import React, {useMemo, useState} from "react";
import {Card, Form, Spinner} from "react-bootstrap";
import {PiBroom} from "react-icons/pi";
import HelpTooltip from "../HelpTooltip.jsx";
import {SeiteLink} from "./Links.jsx";

/**
 * Die wichtigste Ansicht der ganzen Auswertung: Funktionen, die im
 * gewählten Zeitraum KEIN einziges Mal aufgerufen wurden - also
 * Kandidaten zum Abschaffen. Bewusst deutlich abgesetzt von der Tabelle
 * der genutzten Funktionen (eigene, auffällige Karte statt einer
 * Fußnote), mit eigenem Suchfeld, weil die Liste je nach Zeitraum lang
 * werden kann.
 */
export default function NutzungUngenutzteFunktionen({ungenutzt, loading, error}) {
    const [suche, setSuche] = useState("");
    const rows = Array.isArray(ungenutzt) ? ungenutzt : [];

    const gefiltert = useMemo(() => {
        const sortiert = [...rows].sort((a, b) =>
            (a.bezeichnung || a.route || "").localeCompare(b.bezeichnung || b.route || "", "de-DE")
        );
        const begriff = suche.trim().toLocaleLowerCase("de-DE");
        if (!begriff) {
            return sortiert;
        }
        return sortiert.filter((eintrag) =>
            (eintrag.bezeichnung || "").toLocaleLowerCase("de-DE").includes(begriff)
            || (eintrag.route || "").toLocaleLowerCase("de-DE").includes(begriff)
        );
    }, [rows, suche]);

    if (loading) {
        return null;
    }

    if (error) {
        return (
            <Card className="shadow-sm border-danger-subtle mt-4">
                <Card.Body className="text-danger">{error}</Card.Body>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm border-warning-subtle mt-4">
            <Card.Header className="bg-warning-subtle d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span className="d-inline-flex align-items-center gap-2 fw-semibold">
                    <PiBroom size={20} aria-hidden="true" />
                    Von niemandem benutzt
                    <span className="badge bg-warning text-dark">{rows.length}</span>
                    <HelpTooltip placement="right" size={0.85}>
                        Funktionen, die im gewählten Zeitraum kein einziger Mitarbeiter aufgerufen hat.
                        Das sind die besten Kandidaten, um eine Funktion abzuschaffen oder ihren Aufwand
                        zu hinterfragen - je länger der Zeitraum, desto belastbarer die Aussage.
                    </HelpTooltip>
                </span>
                {rows.length > 12 && (
                    <Form.Control
                        type="search"
                        size="sm"
                        placeholder="Funktion suchen…"
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                        style={{maxWidth: 220}}
                        aria-label="Ungenutzte Funktionen durchsuchen"
                    />
                )}
            </Card.Header>
            <Card.Body>
                {rows.length === 0 ? (
                    <div className="text-muted small mb-0">
                        Im gewählten Zeitraum wurde jede bekannte Funktion mindestens einmal benutzt.
                    </div>
                ) : gefiltert.length === 0 ? (
                    <div className="text-muted small mb-0">Keine Funktion passt zur Suche „{suche}“.</div>
                ) : (
                    <div className="d-flex flex-wrap gap-2" style={{maxHeight: 320, overflowY: "auto"}}>
                        {gefiltert.map((eintrag) => (
                            <span
                                key={eintrag.route}
                                className="badge bg-white text-body border border-warning-subtle fw-normal py-2 px-2"
                            >
                                <SeiteLink route={eintrag.route} className="text-decoration-none" />
                            </span>
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
